import React, { useState, useEffect, useRef } from "react";
import { db, auth } from "../../firebase";
import { collection, query, where, orderBy, onSnapshot, addDoc, serverTimestamp, getDoc, doc, updateDoc } from "firebase/firestore";
import { getDocFromServer } from "firebase/firestore";
import BlinkViewer from "./BlinkViewer";

export default function BlinkList() {
    const [statuses, setStatuses] = useState({});
    const [myStatuses, setMyStatuses] = useState([]);
    const [viewingStatuses, setViewingStatuses] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef(null);

    const currentUser = auth.currentUser;

    // 1. Fetch Statuses (Last 24h)
    useEffect(() => {
        if (!currentUser) return;

        // 24 hours ago
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);

        const q = query(
            collection(db, "status"),
            where("timestamp", ">", yesterday),
            orderBy("timestamp", "desc")
        );

        const unsub = onSnapshot(q, (snapshot) => {
            const allStatuses = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));

            // Group by User
            const grouped = {};
            const myOwn = [];

            allStatuses.forEach(s => {
                if (s.userId === currentUser.uid) {
                    myOwn.push(s);
                } else {
                    if (!grouped[s.userId]) grouped[s.userId] = [];
                    grouped[s.userId].push(s);
                }
            });

            setMyStatuses(myOwn);
            setStatuses(grouped);
            setLoading(false);
        });

        return () => unsub();
    }, [currentUser]);

    // 2. Upload Handler
    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.type.startsWith("image/")) {
            alert("Only images are supported for Blink right now.");
            return;
        }

        setIsUploading(true);
        try {
            // Cloudinary Upload (Direct)
            const formData = new FormData();
            formData.append("file", file);
            formData.append("upload_preset", "chatiq");
            formData.append("cloud_name", "dofw2fffg");

            const res = await fetch(`https://api.cloudinary.com/v1_1/dofw2fffg/auto/upload`, {
                method: "POST",
                body: formData
            });
            const data = await res.json();

            if (data.error) throw new Error(data.error.message);

            // Save to Firestore
            await addDoc(collection(db, "status"), {
                userId: currentUser.uid,
                imageUrl: data.secure_url,
                timestamp: serverTimestamp(),
                caption: "" // Can add caption input later
            });

            // Update User Profile (for sync)
            await updateDoc(doc(db, "users", currentUser.uid), {
                hasStatus: true,
                lastStatusTime: serverTimestamp()
            });

        } catch (err) {
            console.error("Upload failed", err);
            alert("Failed to upload status");
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    return (
        <div className="h-full flex flex-col">
            {/* Header */}
            <div className="p-4 border-b border-gray-200 dark:border-white/10 flex justify-between items-center">
                <h2 className="text-xl font-bold dark:text-white">Status</h2>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-6">

                {/* MY STATUS */}
                <div className="flex items-center gap-4 cursor-pointer group hover:bg-gray-100 dark:hover:bg-white/5 p-3 rounded-lg transition-colors" onClick={() => myStatuses.length > 0 ? setViewingStatuses(myStatuses) : fileInputRef.current.click()}>
                    <div className="relative">
                        {/* Avatar or Last Status Thumb */}
                        <div className={`w-14 h-14 rounded-full p-[2px] ${myStatuses.length > 0 ? 'border-2 border-dashed border-[#00a884]' : 'border border-gray-300 dark:border-gray-600'}`}>
                            <img
                                src={myStatuses.length > 0 ? myStatuses[0].imageUrl : (currentUser?.photoURL || "https://via.placeholder.com/150")}
                                alt="My Status"
                                className="w-full h-full rounded-full object-cover"
                            />
                        </div>
                        {/* (+) Icon if empty */}
                        {myStatuses.length === 0 && (
                            <div className="absolute bottom-0 right-0 w-5 h-5 bg-[#00a884] rounded-full flex items-center justify-center border-2 border-white dark:border-[#111b21]">
                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="text-white"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                            </div>
                        )}
                    </div>

                    <div className="flex-1">
                        <h3 className="font-bold text-gray-900 dark:text-gray-100">My Blink</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            {isUploading ? "Sending..." : myStatuses.length > 0 ? "Click to view" : "Click to add update"}
                        </p>
                    </div>
                </div>

                {/* INPUT (Hidden) */}
                <input
                    type="file"
                    ref={fileInputRef}
                    accept="image/*"
                    className="hidden"
                    onChange={handleFileUpload}
                />

                <hr className="border-gray-200 dark:border-white/5" />

                {/* RECENT UPDATES */}
                <div>
                    <h4 className="text-sm font-bold text-[#00a884] mb-4 uppercase tracking-wider">Recent updates</h4>

                    {loading ? (
                        <div className="text-center text-gray-500 text-sm">Loading updates...</div>
                    ) : Object.keys(statuses).length === 0 ? (
                        <div className="text-center text-gray-400 text-sm py-8">No recent updates from friends</div>
                    ) : (
                        <div className="space-y-4">
                            {Object.keys(statuses).map(userId => (
                                <StatusItem
                                    key={userId}
                                    userId={userId}
                                    statusList={statuses[userId]}
                                    onClick={() => setViewingStatuses(statuses[userId])}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* VIEWER MODAL */}
            {viewingStatuses && (
                <BlinkViewer
                    statuses={viewingStatuses}
                    onClose={() => setViewingStatuses(null)}
                />
            )}
        </div>
    );
}

// Sub-component to fetch user details safely
function StatusItem({ userId, statusList, onClick }) {
    const [user, setUser] = useState({ username: "User", photoURL: "" });

    useEffect(() => {
        async function fetchUser() {
            try {
                const snap = await getDoc(doc(db, "users", userId));
                if (snap.exists()) {
                    setUser(snap.data());
                }
            } catch (e) { console.error(e); }
        }
        fetchUser();
    }, [userId]);

    const lastStatus = statusList[0];
    const timeStr = lastStatus.timestamp?.toDate ? lastStatus.timestamp.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "";

    return (
        <div className="flex items-center gap-4 cursor-pointer hover:bg-gray-100 dark:hover:bg-white/5 p-3 rounded-lg transition-colors" onClick={onClick}>
            <div className="relative w-12 h-12 rounded-full p-[2px] border-2 border-[#00a884]">
                <img
                    src={user.photoURL || "https://via.placeholder.com/150"}
                    alt={user.username}
                    className="w-full h-full rounded-full object-cover"
                />
            </div>
            <div>
                <h3 className="font-bold text-gray-900 dark:text-gray-100">{user.username}</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">Today at {timeStr}</p>
            </div>
        </div>
    );
}
